/**
 * Coerce arbitrary backend payload values into render-safe strings.
 *
 * The data-service stores `llm_output`, `error`, and `conclusion` as free-form
 * dicts (`Dict[str, Any]`), and depending on the agent type the same field
 * arrives as a string, an array (e.g. `thoughts.plan` as a list of steps), or
 * a nested object. Rendering those raw crashes React ("Objects are not valid
 * as a React child") or react-markdown (throws on non-string children) and
 * trips the dashboard error boundary. Any UI printing backend-controlled text
 * must go through these helpers.
 */
export function asText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join('\n');
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function nonEmptyObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0;
}

/** Skill payloads vary: harness `{skill_name, input}`, data-service `{name, inputs}`, or a bare string. */
export function skillNameOf(skill: unknown): string {
  if (typeof skill === 'string') return skill;
  const s = skill as any;
  return s?.skill_name || s?.name || 'skill';
}

export function skillInputOf(skill: unknown): Record<string, unknown> | null {
  const s = skill as any;
  const input = s?.input ?? s?.inputs;
  return nonEmptyObject(input) ? input : null;
}
